import type { Value } from '@bagaar/velvet-thunder/components/velvet-checkbox-group';
import Controller from '@ember/controller';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import {
  COOKIE_LOCAL_STORAGE_KEY,
  COOKIES,
  type CookieType,
} from 'walden-fast/constants/cookies';
import type { AppModel } from 'walden-fast/routes/app';
import type ChatService from 'walden-fast/services/chat';
import type ClarityService from 'walden-fast/services/clarity';
import type PostHogService from 'walden-fast/services/posthog';
import type UserService from 'walden-fast/services/user';
import type ImpersonateService from 'walden-fast/services/impersonate';
import { SERVICE_NAMES } from 'walden-fast/utils/constants/services';

export default class AppController extends Controller {
  declare model: AppModel;

  @service('user') declare userService: UserService;
  @service('router') declare routerService: RouterService;
  @service('chat') declare chat: ChatService;
  @service('clarity') declare clarityService: ClarityService;
  @service('posthog') declare postHogService: PostHogService;
  @service('impersonate') declare impersonateService: ImpersonateService;

  COOKIES = COOKIES;

  @tracked showCookieBanner = !window.localStorage.getItem(
    COOKIE_LOCAL_STORAGE_KEY,
  );
  @tracked showCookieSelection = false;
  @tracked cookieSelection: Value | CookieType[] = [COOKIES.ESSENTIAL];

  @action
  logout() {
    void this.userService.logout();
  }

  @action
  changeLanguage(language: string) {
    window.localStorage.setItem('language', language);
    this.routerService.refresh();
  }

  @action
  initialFetch() {
    this.chat.initialFetch();
  }

  get needsToImpersonate() {
    return (
      (this.userService.user?.role === 'WaldenAdministrator' ||
        this.userService.user?.role === 'WaldenUser') &&
      !this.impersonateService.impersonationAccount
    );
  }

  get showChatButton() {
    const IGNORED_ROUTES = ['app.contact.chat', 'app.services.walden-box'];
    return !IGNORED_ROUTES.includes(this.routerService.currentRouteName || '');
  }

  get userCanAccessChatService() {
    return this.userService.canAccessService(SERVICE_NAMES.WALDEN_CHAT);
  }

  @action
  toggleCookieSelection() {
    this.showCookieSelection = !this.showCookieSelection;
  }

  @action
  updateCookieSelection(selection: Value | CookieType[]) {
    this.cookieSelection = selection;
  }

  @action
  saveCookies(all: boolean) {
    if (all) {
      this.cookieSelection = Object.values(COOKIES);
    }

    window.localStorage.setItem(
      COOKIE_LOCAL_STORAGE_KEY,
      JSON.stringify(this.cookieSelection),
    );

    if (
      Array.isArray(this.cookieSelection) &&
      this.cookieSelection.includes(COOKIES.ANALYTICAL)
    ) {
      this.clarityService.start();
      this.postHogService.start();
    }

    this.showCookieBanner = false;
  }
}
